import { Form, Input, Select } from 'antd';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

/** The editable fields shared by "Add customer" and "Edit customer". */
export interface CustomerFormValues {
  name: string;
  phone: string;
  email?: string;
  idType?: string;
  idNumber?: string;
  tin?: string;
  addressLine1?: string;
  postcode?: string;
  city?: string;
  state?: string;
  notes?: string;
}

/**
 * The customer form's fields and their validation, used by both drawers so a
 * rule changed for one is changed for the other.
 *
 * `storedIdNumber` is the value the edit drawer started with — masked unless
 * the user revealed it. A masked value cannot be saved back as a real number,
 * so a changed value that still carries the mask is refused here.
 */
export function CustomerFormFields({
  mode,
  storedIdNumber,
}: {
  mode: 'create' | 'edit';
  storedIdNumber?: string | null;
}): ReactNode {
  const { t } = useTranslation();

  return (
    <>
      <Form.Item
        name="name"
        label={t('fields.name')}
        rules={[{ required: true, min: 2, message: t('customers.form.nameRequired') }]}
      >
        <Input size="large" autoFocus={mode === 'create'} />
      </Form.Item>

      <Form.Item
        name="phone"
        label={t('fields.phone')}
        rules={[{ required: true, min: 7, message: t('customers.form.phoneRequired') }]}
        extra={t('customers.form.phoneHelp')}
      >
        <Input size="large" inputMode="tel" />
      </Form.Item>

      <Form.Item
        name="email"
        label={t('fields.email')}
        rules={[{ type: 'email', message: t('customers.form.emailInvalid') }]}
      >
        <Input size="large" inputMode="email" />
      </Form.Item>

      {/* An ID number is filed under its type, so the type can only be cleared
          together with the number — clearing both is how an ID is removed. */}
      <Form.Item
        name="idType"
        label={t('fields.idType')}
        dependencies={['idNumber']}
        rules={[
          ({ getFieldValue }) => ({
            validator: (_rule, value: string | undefined) => {
              const idNumber = (getFieldValue('idNumber') as string | undefined) ?? '';
              return idNumber.trim().length > 0 && (value === undefined || value.length === 0)
                ? Promise.reject(new Error(t('customers.form.idTypeRequired')))
                : Promise.resolve();
            },
          }),
        ]}
      >
        <Select
          size="large"
          allowClear
          options={[
            { value: 'NRIC', label: t('customers.idTypes.NRIC') },
            { value: 'PASSPORT', label: t('customers.idTypes.PASSPORT') },
            { value: 'BRN', label: t('customers.idTypes.BRN') },
          ]}
        />
      </Form.Item>

      <Form.Item
        name="idNumber"
        label={t('fields.idNumber')}
        extra={
          mode === 'edit' && storedIdNumber !== null && storedIdNumber !== undefined
            ? t('customers.form.idNumberKeep')
            : t('customers.form.idNumberHelp')
        }
        rules={[
          {
            validator: (_rule, value: string | undefined) => {
              const v = value ?? '';
              return v !== (storedIdNumber ?? '') && v.includes('*')
                ? Promise.reject(new Error(t('customers.form.idNumberMasked')))
                : Promise.resolve();
            },
          },
        ]}
      >
        <Input size="large" />
      </Form.Item>

      {/* e-Invoice fields: AutoCount submits to MyInvois on NKS's behalf, and
          every document we hand over must already be complete (CLAUDE.md §13). */}
      <Form.Item name="tin" label={t('fields.tin')} extra={t('customers.form.tinHelp')}>
        <Input size="large" />
      </Form.Item>

      <Form.Item name="addressLine1" label={t('fields.address')}>
        <Input size="large" />
      </Form.Item>
      <Form.Item name="postcode" label={t('fields.postcode')}>
        <Input size="large" inputMode="numeric" />
      </Form.Item>
      <Form.Item name="city" label={t('fields.city')}>
        <Input size="large" />
      </Form.Item>
      <Form.Item name="state" label={t('fields.state')}>
        <Input size="large" />
      </Form.Item>
      <Form.Item name="notes" label={t('fields.notes')}>
        <Input.TextArea rows={3} />
      </Form.Item>
    </>
  );
}
